// src/pages/AmalanBaikDashboard.jsx
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "react-toastify";
import API from "../../services/API";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";

// ==================== API HELPERS ====================
const getTokenHeaders = () => ({
    token: localStorage.getItem("token"),
});

const fetchAmalanSummary = (params = {}) =>
    API.get("/analytics/amalanBaik/summary", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanCategorySummary = (params = {}) =>
    API.get("/analytics/amalanBaik/byCategory", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanTopTeachers = (params = {}) =>
    API.get("/goodDeeds/topFiveTeachersAmalanBaik", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanTopStudentsMonth = (params = {}) =>
    API.get("/goodDeeds/topFiveStudentsAmalanBaik", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

// ==================== COLORS ====================
const COLORS = [
    "#6366F1",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#0EA5E9",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#F97316",
    "#84CC16",
    "#3B82F6",
    "#10B981",
    "#EAB308",
    "#D946EF",
    "#06B6D4",
];

const PODIUM_STYLE = {
    1: {
        label: "1st",
        icon: "🥇",
        height: 230,
        gradient: "linear-gradient(180deg, #fff7cc 0%, #facc15 100%)",
        border: "#eab308",
        shadow: "0 22px 50px rgba(234, 179, 8, 0.22)",
    },
    2: {
        label: "2nd",
        icon: "🥈",
        height: 190,
        gradient: "linear-gradient(180deg, #f8fafc 0%, #cbd5e1 100%)",
        border: "#94a3b8",
        shadow: "0 18px 42px rgba(100, 116, 139, 0.18)",
    },
    3: {
        label: "3rd",
        icon: "🥉",
        height: 170,
        gradient: "linear-gradient(180deg, #ffedd5 0%, #fb923c 100%)",
        border: "#ea580c",
        shadow: "0 18px 42px rgba(234, 88, 12, 0.18)",
    },
};

// ==================== UTILS ====================
const formatNumber = (value) => {
    const number = Number(value || 0);
    return number.toLocaleString("en-MY");
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const getStudentPoints = (student) =>
    Number(student?.total_deeds ?? student?.amalan_baik_count ?? student?.total ?? 0);

const getTeacherPoints = (teacher) =>
    Number(teacher?.total_deeds ?? teacher?.amalan_baik_count ?? teacher?.total ?? 0);

const getInitial = (name) => {
    if (!name) return "?";
    return String(name).trim().charAt(0).toUpperCase();
};

// ==================== COMPONENTS ====================
const PageShell = ({ children }) => (
    <div
        className="container-fluid"
        style={{
            minHeight: "100vh",
            background:
                "radial-gradient(circle at top left, rgba(239,68,68,0.08), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
            padding: "1.25rem",
        }}
    >
        {children}
    </div>
);

const SectionCard = ({ title, subtitle, action, children, style }) => (
    <div
        className="card border-0 mb-4"
        style={{
            borderRadius: 24,
            overflow: "hidden",
            boxShadow: "0 16px 45px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(226, 232, 240, 0.9)",
            ...style,
        }}
    >
        {(title || subtitle || action) && (
            <div
                className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3"
                style={{
                    padding: "1.15rem 1.25rem",
                    borderBottom: "1px solid #e2e8f0",
                    background: "rgba(255,255,255,0.85)",
                }}
            >
                <div>
                    {title && (
                        <h5
                            className="mb-1"
                            style={{
                                fontWeight: 900,
                                color: "#0f172a",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {title}
                        </h5>
                    )}
                    {subtitle && (
                        <div
                            style={{
                                color: "#64748b",
                                fontSize: "0.88rem",
                                fontWeight: 600,
                            }}
                        >
                            {subtitle}
                        </div>
                    )}
                </div>

                {action}
            </div>
        )}

        <div style={{ padding: "1.25rem", background: "#fff" }}>{children}</div>
    </div>
);

const LoadingState = () => (
    <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{
            minHeight: 300,
            color: "#64748b",
        }}
    >
        <div
            className="spinner-border text-danger mb-3"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
        />
        <div style={{ fontWeight: 800, color: "#334155" }}>Loading dashboard...</div>
        <small>Fetching latest Amalan Baik analytics</small>
    </div>
);

const EmptyState = ({ title = "No data available", subtitle = "Try another month." }) => (
    <div
        className="text-center"
        style={{
            padding: "3rem 1rem",
            borderRadius: 20,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
        }}
    >
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>📭</div>
        <h6 style={{ fontWeight: 900, color: "#334155" }}>{title}</h6>
        <div style={{ color: "#64748b", fontSize: "0.9rem" }}>{subtitle}</div>
    </div>
);

const KpiCard = ({ label, value, subtext, icon, tone = "red" }) => {
    const tones = {
        red: {
            bg: "linear-gradient(135deg, #fff1f2 0%, #ffffff 70%)",
            iconBg: "#ffe4e6",
            iconColor: "#e11d48",
            border: "#fecdd3",
        },
        green: {
            bg: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 70%)",
            iconBg: "#dcfce7",
            iconColor: "#16a34a",
            border: "#bbf7d0",
        },
        blue: {
            bg: "linear-gradient(135deg, #eff6ff 0%, #ffffff 70%)",
            iconBg: "#dbeafe",
            iconColor: "#2563eb",
            border: "#bfdbfe",
        },
        purple: {
            bg: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 70%)",
            iconBg: "#ede9fe",
            iconColor: "#7c3aed",
            border: "#ddd6fe",
        },
    };

    const theme = tones[tone] || tones.red;

    return (
        <div className="col-12 col-sm-6 col-xl-3">
            <div
                style={{
                    height: "100%",
                    borderRadius: 22,
                    padding: "1rem",
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
                }}
            >
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: "#64748b",
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                marginBottom: 8,
                            }}
                        >
                            {label}
                        </div>

                        <div
                            style={{
                                color: "#0f172a",
                                fontSize: "1.85rem",
                                fontWeight: 950,
                                lineHeight: 1,
                                letterSpacing: "-0.05em",
                            }}
                        >
                            {value}
                        </div>

                        {subtext && (
                            <div
                                style={{
                                    color: "#64748b",
                                    fontSize: "0.78rem",
                                    marginTop: 8,
                                    fontWeight: 600,
                                }}
                            >
                                {subtext}
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 16,
                            background: theme.iconBg,
                            color: theme.iconColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.25rem",
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 15px 35px rgba(15,23,42,0.12)",
                borderRadius: 14,
                padding: "0.8rem",
            }}
        >
            <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>{label}</div>
            {payload.map((item, idx) => (
                <div
                    key={idx}
                    style={{
                        color: item.color,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                    }}
                >
                    {item.name}: {formatNumber(item.value)}
                </div>
            ))}
        </div>
    );
};

const PersonAvatar = ({ name, color = "#ef4444" }) => (
    <div
        style={{
            width: 52,
            height: 52,
            borderRadius: 18,
            background: `linear-gradient(135deg, ${color}, #111827)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 950,
            fontSize: "1.15rem",
            boxShadow: "0 12px 25px rgba(15,23,42,0.18)",
            flexShrink: 0,
        }}
    >
        {getInitial(name)}
    </div>
);

const PodiumCard = ({ item, rank, type = "student" }) => {
    const theme = PODIUM_STYLE[rank];
    const name = type === "teacher" ? item?.teacher_name : item?.student_name;
    const subtitle = type === "teacher" ? "Teacher" : item?.class_name || "Student";
    const points = type === "teacher" ? getTeacherPoints(item) : getStudentPoints(item);

    return (
        <div
            className="d-flex flex-column align-items-center justify-content-between text-center"
            style={{
                width: 210,
                minHeight: theme.height,
                borderRadius: 26,
                padding: "1rem",
                background: theme.gradient,
                border: `1px solid ${theme.border}`,
                boxShadow: theme.shadow,
                transform: rank === 1 ? "translateY(-10px)" : "translateY(0)",
            }}
        >
            <div
                style={{
                    alignSelf: "stretch",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                }}
            >
                <span
                    style={{
                        background: "rgba(255,255,255,0.75)",
                        border: `1px solid ${theme.border}`,
                        color: "#0f172a",
                        fontWeight: 950,
                        borderRadius: 999,
                        padding: "5px 10px",
                        fontSize: "0.8rem",
                    }}
                >
                    {theme.icon} {theme.label}
                </span>

                <span
                    style={{
                        background: "#0f172a",
                        color: "#fff",
                        fontWeight: 950,
                        borderRadius: 999,
                        padding: "5px 10px",
                        fontSize: "0.78rem",
                    }}
                >
                    {formatNumber(points)} pts
                </span>
            </div>

            <div className="my-3">
                <PersonAvatar name={name} color={theme.border} />
            </div>

            <div>
                <div
                    style={{
                        fontWeight: 950,
                        color: "#111827",
                        fontSize: "0.98rem",
                        textTransform: "uppercase",
                        lineHeight: 1.25,
                    }}
                >
                    {name || "-"}
                </div>

                <div
                    style={{
                        color: "#475569",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        marginTop: 4,
                    }}
                >
                    {subtitle || "-"}
                </div>
            </div>
        </div>
    );
};

const RankingList = ({ items, type = "student", startRank = 4 }) => {
    if (!items?.length) return null;

    return (
        <div className="d-flex flex-column gap-2">
            {items.map((item, idx) => {
                const rank = startRank + idx;
                const name = type === "teacher" ? item?.teacher_name : item?.student_name;
                const subtitle = type === "teacher" ? "Teacher" : item?.class_name;
                const points = type === "teacher" ? getTeacherPoints(item) : getStudentPoints(item);

                return (
                    <div
                        key={`${type}-${rank}-${name || idx}`}
                        className="d-flex align-items-center justify-content-between gap-3"
                        style={{
                            borderRadius: 18,
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            padding: "0.85rem 1rem",
                        }}
                    >
                        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                            <div
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 14,
                                    background: "#0f172a",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 950,
                                    flexShrink: 0,
                                }}
                            >
                                #{rank}
                            </div>

                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        color: "#0f172a",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {name || "-"}
                                </div>
                                {subtitle && (
                                    <div
                                        style={{
                                            color: "#64748b",
                                            fontSize: "0.8rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {subtitle}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div
                            style={{
                                color: "#dc2626",
                                fontWeight: 950,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {formatNumber(points)} pts
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const PodiumSection = ({ title, subtitle, items, type }) => {
    const top = safeArray(items);
    if (top.length === 0) return null;

    const podiumItems = [
        { item: top[1], rank: 2 },
        { item: top[0], rank: 1 },
        { item: top[2], rank: 3 },
    ].filter((x) => Boolean(x.item));

    return (
        <SectionCard title={title} subtitle={subtitle}>
            <div
                className="d-flex justify-content-center align-items-end flex-wrap"
                style={{ gap: "1rem", paddingTop: 12, paddingBottom: 20 }}
            >
                {podiumItems.map(({ item, rank }) => (
                    <PodiumCard
                        key={`${type}-${rank}-${item?.student_uuid || item?.teacher_id || item?.teacher_name}`}
                        item={item}
                        rank={rank}
                        type={type}
                    />
                ))}
            </div>

            <RankingList items={top.slice(3)} type={type} startRank={4} />
        </SectionCard>
    );
};

// ==================== MAIN COMPONENT ====================
const AmalanBaikDashboard = () => {
    const today = new Date();
    const currentMonth = today.toISOString().slice(0, 7);

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topOverallStudents, setTopOverallStudents] = useState([]);
    const [topOverallTeachers, setTopOverallTeachers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(false);

    const filteredCategories = useMemo(() => {
        return safeArray(categories).filter(
            (category) => String(category?.category || "").toLowerCase() !== "attendance"
        );
    }, [categories]);

    const selectedCategoryData = useMemo(() => {
        if (!selectedCategory) return null;
        return filteredCategories.find((item) => item.category === selectedCategory) || null;
    }, [filteredCategories, selectedCategory]);

    const categoryBarData = useMemo(() => {
        return filteredCategories
            .slice()
            .sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
            .slice(0, 8);
    }, [filteredCategories]);

    const kpis = useMemo(() => {
        if (!overview) return [];

        return [
            {
                label: "Total Amalan Baik",
                value: formatNumber(overview.total_ops),
                subtext: "Total records this month",
                icon: "🌟",
                tone: "red",
            },
            {
                label: "Students",
                value: formatNumber(overview.unique_students),
                subtext: "Unique students this month",
                icon: "🎓",
                tone: "blue",
            },
            {
                label: "Teachers",
                value: formatNumber(overview.unique_teachers),
                subtext: "Teachers involved this month",
                icon: "🧑‍🏫",
                tone: "purple",
            },
            {
                label: "Categories",
                value: formatNumber(filteredCategories.length),
                subtext: "Active categories this month",
                icon: "🏷️",
                tone: "green",
            },
        ];
    }, [overview, filteredCategories.length]);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setLoading(true);

            try {
                const [
                    resSummary,
                    resCategories,
                    resTopTeachers,
                    resTopStudents,
                ] = await Promise.allSettled([
                    fetchAmalanSummary({ month: selectedMonth }),
                    fetchAmalanCategorySummary({ month: selectedMonth }),
                    fetchAmalanTopTeachers({
                        scope: "month",
                        month: selectedMonth,
                        limit_top: 5,
                    }),
                    fetchAmalanTopStudentsMonth({
                        scope: "month",
                        month: selectedMonth,
                        limit_top: 5,
                    }),
                ]);

                if (!isMounted) return;

                if (
                    resSummary.status === "fulfilled" &&
                    resSummary.value?.data?.response_code === 1000
                ) {
                    setOverview(resSummary.value.data.data?.overview || null);
                    setTrend(resSummary.value.data.data?.trend || []);
                } else {
                    setOverview(null);
                    setTrend([]);
                    toast.error(
                        resSummary.value?.data?.message || "Failed to load Amalan Baik summary"
                    );
                }

                if (
                    resCategories.status === "fulfilled" &&
                    resCategories.value?.data?.response_code === 1000
                ) {
                    setCategories(resCategories.value.data.data || []);
                } else {
                    setCategories([]);
                }

                if (
                    resTopTeachers.status === "fulfilled" &&
                    resTopTeachers.value?.data?.response_code === 1000
                ) {
                    setTopOverallTeachers(resTopTeachers.value.data.data || []);
                } else {
                    setTopOverallTeachers([]);
                }

                if (
                    resTopStudents.status === "fulfilled" &&
                    resTopStudents.value?.data?.response_code === 1000
                ) {
                    setTopOverallStudents(resTopStudents.value.data.data || []);
                } else {
                    setTopOverallStudents([]);
                }
            } catch (err) {
                if (!isMounted) return;

                console.error("❌ Failed to fetch Amalan Baik analytics:", err);
                toast.error(err?.message || "Failed to fetch Amalan Baik analytics");

                setOverview(null);
                setTrend([]);
                setCategories([]);
                setTopOverallStudents([]);
                setTopOverallTeachers([]);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [selectedMonth]);

    return (
        <Fragment>
            <PageShell>
                <div
                    className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4"
                    style={{
                        background:
                            "linear-gradient(135deg, #991b1b 0%, #dc2626 45%, #f97316 100%)",
                        borderRadius: 28,
                        padding: "1.5rem",
                        color: "#fff",
                        boxShadow: "0 24px 60px rgba(220, 38, 38, 0.25)",
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                background: "rgba(255,255,255,0.16)",
                                border: "1px solid rgba(255,255,255,0.26)",
                                borderRadius: 999,
                                padding: "6px 12px",
                                fontSize: "0.8rem",
                                fontWeight: 900,
                                marginBottom: 12,
                            }}
                        >
                            🌟 Positive Behaviour Analytics
                        </div>

                        <h2
                            className="mb-1"
                            style={{
                                fontWeight: 950,
                                letterSpacing: "-0.04em",
                                fontSize: "2rem",
                            }}
                        >
                            Amalan Baik Dashboard
                        </h2>

                        <div
                            style={{
                                color: "rgba(255,255,255,0.82)",
                                fontWeight: 600,
                                maxWidth: 760,
                            }}
                        >
                            Monitor good deeds, student recognition, teacher contributions,
                            and category trends for the selected month.
                        </div>
                    </div>

                    <div
                        style={{
                            background: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            borderRadius: 18,
                            padding: "0.85rem",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "rgba(255,255,255,0.75)",
                                marginBottom: 5,
                            }}
                        >
                            Reporting Month
                        </div>
                        <input
                            type="month"
                            className="form-control"
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                setSelectedCategory(null);
                            }}
                            style={{
                                borderRadius: 14,
                                border: "none",
                                minWidth: 180,
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        />
                    </div>
                </div>

                {loading && !overview ? (
                    <SectionCard>
                        <LoadingState />
                    </SectionCard>
                ) : (
                    <>
                        {kpis.length > 0 && (
                            <div className="row g-3 mb-4">
                                {kpis.map((item) => (
                                    <KpiCard key={item.label} {...item} />
                                ))}
                            </div>
                        )}

                        <div className="row">
                            <div className="col-12 col-xxl-6">
                                <PodiumSection
                                    title="Top 5 Students"
                                    subtitle={`Highest Amalan Baik points for ${selectedMonth}`}
                                    items={topOverallStudents}
                                    type="student"
                                />
                            </div>

                            <div className="col-12 col-xxl-6">
                                <PodiumSection
                                    title="Top 5 Teachers"
                                    subtitle={`Highest teacher contributions for ${selectedMonth}`}
                                    items={topOverallTeachers}
                                    type="teacher"
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-xl-8">
                                <SectionCard
                                    title={`Monthly Trend (${selectedMonth})`}
                                    subtitle="Daily total Amalan Baik records"
                                >
                                    {trend.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <LineChart
                                                data={trend}
                                                margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    tickMargin={10}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    allowDecimals={false}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="total"
                                                    stroke="#EF4444"
                                                    strokeWidth={3}
                                                    dot={{ r: 3 }}
                                                    activeDot={{ r: 6 }}
                                                    name="Total Amalan Baik"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No trend data"
                                            subtitle="No daily trend was found for this month."
                                        />
                                    )}
                                </SectionCard>
                            </div>

                            <div className="col-12 col-xl-4">
                                <SectionCard
                                    title="Category Mix"
                                    subtitle={
                                        selectedCategoryData
                                            ? `${selectedCategoryData.category}: ${formatNumber(
                                                  selectedCategoryData.total
                                              )} records`
                                            : "Click a slice to highlight a category"
                                    }
                                    action={
                                        selectedCategory ? (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setSelectedCategory(null)}
                                                style={{ borderRadius: 999, fontWeight: 800 }}
                                            >
                                                Clear Filter
                                            </button>
                                        ) : null
                                    }
                                >
                                    {filteredCategories.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <PieChart>
                                                <Pie
                                                    data={filteredCategories}
                                                    dataKey="total"
                                                    nameKey="category"
                                                    cx="50%"
                                                    cy="48%"
                                                    innerRadius={62}
                                                    outerRadius={112}
                                                    paddingAngle={3}
                                                    labelLine={false}
                                                    label={({ percent }) =>
                                                        `${(percent * 100).toFixed(0)}%`
                                                    }
                                                    onClick={(data) => {
                                                        if (selectedCategory === data.category) {
                                                            setSelectedCategory(null);
                                                        } else {
                                                            setSelectedCategory(data.category);
                                                            toast.info(`Filtered by category: ${data.category}`);
                                                        }
                                                    }}
                                                >
                                                    {filteredCategories.map((entry, idx) => (
                                                        <Cell
                                                            key={entry.category || idx}
                                                            fill={COLORS[idx % COLORS.length]}
                                                            style={{
                                                                cursor: "pointer",
                                                                opacity:
                                                                    selectedCategory &&
                                                                    selectedCategory !== entry.category
                                                                        ? 0.35
                                                                        : 1,
                                                            }}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend verticalAlign="bottom" align="center" layout="horizontal" />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No category data"
                                            subtitle="No category breakdown was found for this month."
                                        />
                                    )}
                                </SectionCard>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <SectionCard
                                    title="Top Categories"
                                    subtitle="Highest Amalan Baik categories excluding Attendance"
                                >
                                    {categoryBarData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <BarChart
                                                data={categoryBarData}
                                                layout="vertical"
                                                margin={{ top: 5, right: 20, left: 30, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    type="number"
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    allowDecimals={false}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="category"
                                                    width={140}
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Bar
                                                    dataKey="total"
                                                    name="Total"
                                                    radius={[0, 10, 10, 0]}
                                                    fill="#ef4444"
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No category ranking"
                                            subtitle="Category ranking will appear after records are available."
                                        />
                                    )}
                                </SectionCard>
                            </div>
                        </div>
                    </>
                )}
            </PageShell>
        </Fragment>
    );
};

export default AmalanBaikDashboard;